import 'core-js/stable';
import 'regenerator-runtime/runtime';
import './scss/custom.scss';
import * as Sentry from '@sentry/browser';
import {
  addMinutes,
  differenceInMilliseconds,
  formatDistanceStrict,
  isPast,
  parseISO,
} from 'date-fns';
import Api from './controllers/api';
import Analytics from './controllers/analytics';
import Logger from 'src/utils/logger';
import Waiting from 'src/controllers/waiting';
import View from 'src/controllers/view';
import { initVideo } from 'src/controllers/video';
import Verify from 'src/controllers/verify';

if (process.env.STAGE === 'dev') {
  // In dev abilito i log di default
  window.LOG_LEVEL = 'debug';
}

const REDIRECT_LINK = `${process.env.LP_LINK}?error=true&ErrMsg=Utente+non+valido`;

let START_EVENT;
let END_EVENT;
try {
  /**
   * Se sono in dev setto lo start_event ad un minuto dalla data di accesso
   * utente e lo switchoff a 12 minuti dalla data di accesso
   */

  const currentDate = new Date();
  START_EVENT = process.env.START_DATE
    ? parseISO(process.env.START_DATE)
    : addMinutes(currentDate, 1);
  END_EVENT = process.env.END_DATE
    ? parseISO(process.env.END_DATE)
    : addMinutes(currentDate, 12);
} catch (err) {
  Sentry.captureException(err);
  Logger('error', err);
  // Se fallisce qualcosa setto io le date
  START_EVENT = new Date(2022, 5, 15, 9, 0, 0);
  END_EVENT = new Date(2022, 5, 15, 13, 0, 0, 0);
}

Logger('debug', `Event will go live at ${START_EVENT}`);
Logger('debug', `Event will go off at ${END_EVENT}`);

let switchOffTimeout = null;

if (process.env.STAGE === 'dev') {
  window.setSwitchOff = (endDate) => {
    clearTimeout(switchOffTimeout);
    switchOffTimeout = setSwitchOffTimeout(endDate);
  };
}

Sentry.init({
  dsn: process.env.SENTRY_DSN,
  integrations: [Sentry.browserTracingIntegration()],
  tracesSampleRate: 0,
  environment: process.env.STAGE,
  release: process.env.VERSION,
  denyUrls: ['local-streaming.ilsole24ore.com'],
});

const setSwitchOffTimeout = (endDate) => {
  const now = new Date();
  const diffInMilliseconds = differenceInMilliseconds(endDate, now.getTime());
  const formattedDistance = formatDistanceStrict(now, endDate, {
    unit: 'minute',
  });
  Logger(
    'debug',
    `END EVENT is at ${endDate}, switching off in ${formattedDistance} (${diffInMilliseconds} milliseconds)`,
  );
  switchOffTimeout = setTimeout(View.switchOff, diffInMilliseconds);
};

const initApp = async () => {
  window.analyticsJob = null;
  let userId;
  try {
    // Faccio il check dell'utente
    Sentry.addBreadcrumb({
      category: 'auth',
      message: 'App ready, checking user auth...',
      level: 'info',
    });
    if (
      process.env.STAGE === 'dev' &&
      ['/loadTest.html', '/diretta/loadTest.html'].includes(
        window.location.pathname,
      )
    ) {
      Logger('debug', 'Load test mode...');
      userId = '{72FB48AF-CEE8-4B55-8208-F2D489EE6A01}';
    } else {
      userId = await Api.getUserId();
    }
    if (!userId) {
      // Utente non valido, lo butto fuori.
      Sentry.addBreadcrumb({
        category: 'auth',
        message: 'Invalid user, bail out...',
        level: 'warning',
      });
      Logger('warn', `Invalid user, bail out...`);
      window.location = REDIRECT_LINK;
      return;
    }
    // Setto lo user su Sentry
    Sentry.addBreadcrumb({
      category: 'auth',
      message: 'The user is valid, starting Analytics...',
      level: 'info',
    });
    Sentry.setUser({ id: userId });
    // Faccio il setup del player
    initVideo();
    if (isPast(END_EVENT)) {
      // L'evento è concluso faccio solo switchoff
      View.switchOff();
      return;
    }
    if (!isPast(START_EVENT)) {
      // Sono in waitingRoom, faccio partire countdown + video embed
      Waiting.init(userId, END_EVENT);
      if (!isPast(END_EVENT)) {
        // Faccio il setup per lo switchoff dell'evento
        setSwitchOffTimeout(END_EVENT);
      }
      return;
    }
    // Non sono in waitingRoom, carico iframe streaming
    View.hideCountdown();
    View.showDiretta();
    if (process.env.MANUAL_CHECKS_ENABLED === 'true') {
      Logger(
        'debug',
        `Manual checks are enabled ${process.env.MANUAL_CHECKS_ENABLED}`,
      );
      Verify.init(userId);
    }
    // Parte il cron degli analytics
    window.analyticsJob = Analytics.init(userId, END_EVENT);
    if (!isPast(END_EVENT)) {
      // Faccio il setup per lo switchoff dell'evento
      setSwitchOffTimeout(END_EVENT);
    }
  } catch (err) {
    Sentry.captureException(err);
    // Se l'id non è definito, faccio redirect alla pagina di iscrizione
    if (!userId) {
      window.location = REDIRECT_LINK;
      return;
    }
    // Nel caso il job non sia partito o si sia interrotto, lo faccio ripartire
    if (!window.analyticsJob) {
      initApp();
    }
  }
};
document.addEventListener('DOMContentLoaded', initApp);
